/* eslint-disable no-use-before-define */
import { FC, useEffect, useState } from 'react';
import { Slider, Rail, Handles, Tracks, Ticks } from 'react-compound-slider';
import { useConnector } from 'react-instantsearch-hooks-web';
import connectRange from 'instantsearch.js/es/connectors/range/connectRange';
import type { RangeConnectorParams, RangeWidgetDescription } from 'instantsearch.js/es/connectors/range/connectRange';

import { Handle } from './Handle';
import { formatMoney } from '../../../../utils/money';

const useRangeSlider = (props?: RangeConnectorParams) => {
  return useConnector<RangeConnectorParams, RangeWidgetDescription>(connectRange, props);
};

function handleInfinityValues(values: number[], range: { min: number; max: number }) {
  const minValue = Number.isFinite(values[0]) ? values[0] : range.min;
  const maxValue = Number.isFinite(values[1]) ? values[1] : range.max;

  return [minValue, maxValue];
}

export const RangeSlider: FC<RangeConnectorParams> = (props) => {
  const { attribute } = props;
  const { range, canRefine, refine, start } = useRangeSlider(props);
  const [currentValues, setCurrentValues] = useState(start || [range.min, range.max]);
  const [sliderInteraction, setSliderInteraction] = useState(false);

  useEffect(() => {
    if (!sliderInteraction && canRefine) {
      const newValues = handleInfinityValues([range.min, range.max], range);

      setCurrentValues(newValues);
    }

    setSliderInteraction(false);
  }, [range.min, range.max, canRefine]);

  useEffect(() => {
    const newValues = handleInfinityValues(start || [range.min, range.max], range);

    setCurrentValues(newValues);
  }, [start]);

  return (
    <Slider
      mode={2}
      step={1}
      domain={[range.min as number, range.max as number]}
      values={currentValues as number[]}
      disabled={!canRefine}
      onChange={(values) => {
        if (sliderInteraction) {
          refine(values);
          setCurrentValues(values);
        }
      }}
      onSlideStart={() => setSliderInteraction(true)}
      className="ais-RangeSlider"
    >
      <Rail>{({ getRailProps }) => <div className="slider-rail" {...getRailProps()} />}</Rail>

      <Tracks left={false} right={false}>
        {({ tracks, getTrackProps }) => (
          <div>
            {tracks.map(({ source, target }, id) => (
              <div
                key={id}
                className="slider-track"
                style={{
                  left: `${source.percent}%`,
                  width: `${target.percent - source.percent}%`,
                }}
                {...getTrackProps()}
              />
            ))}
          </div>
        )}
      </Tracks>

      <Handles>
        {({ handles, getHandleProps }) => (
          <div>
            {handles.map((handle) => (
              <Handle
                key={handle.id}
                handle={handle}
                domain={[range.min as number, range.max as number]}
                getHandleProps={getHandleProps}
              />
            ))}
          </div>
        )}
      </Handles>

      <Ticks values={currentValues as number[]} count={2}>
        {({ ticks }) => (
          <div>
            {ticks.map(({ value, percent }, id) => (
              <div key={id} className="slider-tick" style={{ left: `${percent}%` }}>
                <span style={{ color: '#e2a400', marginRight: 4 }}>
                  {attribute.startsWith('price') ? formatMoney(value) : value}
                </span>
              </div>
            ))}
          </div>
        )}
      </Ticks>
    </Slider>
  );
};
